<template>
  <div
    class="rounded-extra-small first:rounded-t-large last:rounded-b-large p-3"
    :class="[
      comment.isConversationNote && 'bg-tertiary-container',
      !comment.isConversationNote && 'bg-surface-container-high',
    ]"
  >
    <div class="flex flex-row items-center" :class="[isFirst && 'mb-2', !isFirst && '-mt-1 mb-1']">
      <BaseMemberAvatar
        v-if="isFirst && !comment.isConversationNote"
        size="32"
        :member="comment.author"
      />
      <div
        v-if="isFirst && comment.isConversationNote"
        class="bg-tertiary flex h-8 w-8 shrink-0 flex-row items-center justify-center rounded-full"
      >
        <BaseIcon class="text-on-tertiary" size="20">group</BaseIcon>
      </div>
      <div class="flex flex-col" :class="[isFirst && 'ml-2']">
        <span v-if="isFirst && !comment.isConversationNote" class="text-label-large font-medium">{{
          comment.author.firstName
        }}</span>
        <span v-if="isFirst && comment.isConversationNote" class="text-label-large font-medium">
          {{ $t('comment.conversation-note.label') }}
        </span>
        <TimeAgo
          class="text-body-small text-on-surface-variant"
          :date="comment.createdAt"
          :class="[isFirst && '-mt-0.5']"
        />
      </div>
      <div
        v-if="
          isEditable ||
          (canPromoteToConversationNote && !comment.isConversationNote && comment.isShared)
        "
        class="-mr-2 ml-auto flex flex-row gap-1 self-start"
      >
        <BaseIconButton
          v-if="!showIsShared && isOwner && isEditable && !comment.isConversationNote"
          v-tooltip="
            comment.isShared
              ? $t('comment.share.tooltip.shared')
              : $t('comment.share.tooltip.unshared')
          "
          size="XS"
          :icon="comment.isShared ? 'visibility' : 'visibility_off'"
          color="filled"
          toggle
          :selected="editableComment.isShared"
          @click="
            () => {
              editableComment.isShared = !editableComment.isShared
              saveComment()
            }
          "
        />
        <BaseMenu v-if="menuItems.length > 0" :items="menuItems" v-slot="{ open }">
          <BaseIconButton size="XS" icon="more_vert" :force-pressed="open" />
        </BaseMenu>
      </div>
      <div v-if="!isEditable && isOwner" class="ml-auto flex flex-row gap-1 self-start">
        <BaseIcon
          class="text-on-surface-variant"
          size="20"
          v-tooltip="
            comment.isShared
              ? $t('comment.share.tooltip.shared')
              : $t('comment.share.tooltip.unshared')
          "
          >{{ comment.isShared ? 'visibility' : 'visibility_off' }}</BaseIcon
        >
      </div>
    </div>

    <div v-if="isEditing">
      <WysiwygTextField v-model="editableComment.text" autofocus hide-headlines />
      <div class="mt-2 flex w-full flex-row justify-end gap-1">
        <BaseTextButton @click="cancelEdit" size="XS" class="text-tertiary!">{{
          $t('common.cancel')
        }}</BaseTextButton>
        <BaseTextButton
          @click="saveComment"
          size="XS"
          :color="comment.isConversationNote ? 'filledTertiary' : 'filled'"
          >{{ $t('common.save') }}</BaseTextButton
        >
      </div>
    </div>
    <div
      v-if="!isEditing"
      :class="[
        isEditable &&
          'hover:bg-surface-container rounded-small cursor-pointer hover:-m-1 hover:p-1',
      ]"
      @click="isEditable ? editComment() : null"
    >
      <WysiwygTextFieldReadOnly :value="comment.text" paragraph-class="text-body-medium" />
    </div>

    <BaseDialog
      type="basic"
      :title="$t('comment.delete.confirmation')"
      :description="$t('comment.delete.description')"
      :is-open="isOpenDeleteCommentDialog"
      @close="closeDeleteCommentDialog"
    >
      <template #dismiss>
        <BaseTextButton color="text" @click="closeDeleteCommentDialog">{{
          $t('common.cancel')
        }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton color="text" @click="deleteComment">{{
          $t('comment.delete.action')
        }}</BaseTextButton>
      </template>
    </BaseDialog>
  </div>
</template>
<script setup lang="ts">
  import { computed, toRef } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useUserStore } from '../../../stores/user.store'
  import { useComment } from '../../composables/use-comment'
  import type { Comment } from '../../composables/use-comments'
  import BaseMemberAvatar from '../base/avatar/BaseMemberAvatar.vue'
  import BaseIconButton from '../base/button/icon-button/BaseIconButton.vue'
  import TimeAgo from '../base/time-ago/TimeAgo.vue'
  import WysiwygTextFieldReadOnly from '../base/wysiwyg/WysiwygTextFieldReadOnly.vue'
  import WysiwygTextField from '../base/wysiwyg/WysiwygTextField.vue'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'
  import BaseMenu from '../base/menu/BaseMenu.vue'
  import type { MenuItemGroup } from '../base/menu/MenuItem'
  import BaseDialog from '../base/dialog/BaseDialog.vue'
  import BaseIcon from '../base/icon/BaseIcon.vue'

  interface Props {
    comment: Comment
    isFirst: boolean
    showIsShared?: boolean
    isEditable?: boolean
    canPromoteToConversationNote?: boolean
  }

  const props = defineProps<Props>()

  const emits = defineEmits<{
    (e: 'update:comment', value: Comment): void
    (e: 'delete:comment'): void
  }>()

  const user = useUserStore()
  const { t } = useI18n()

  const {
    isEditing,
    editableComment,
    cancelEdit,
    saveComment,
    editComment,
    deleteComment,
    promoteToConversationNote,
    openDeleteCommentDialog,
    closeDeleteCommentDialog,
    isOpenDeleteCommentDialog,
  } = useComment(
    toRef(props, 'comment'),
    (comment) => emits('update:comment', comment),
    () => emits('delete:comment'),
  )

  const isOwner = computed(() => user.user.id === props.comment.author.id)

  const menuItems = computed(() => {
    const menuItems: MenuItemGroup[] = []
    if (isOwner.value && props.isEditable) {
      menuItems.push({
        items: [
          {
            id: 'edit',
            leadingIcon: 'edit',
            title: 'Bearbeiten',
            callback: () => {
              editComment()
            },
          },
        ],
      })
    }
    if (
      props.canPromoteToConversationNote &&
      !props.comment.isConversationNote &&
      props.comment.isShared
    ) {
      menuItems.push({
        items: [
          {
            id: 'promote-to-conversation-note',
            leadingIcon: 'add_notes',
            title: t('comment.conversation-note.promote.action'),
            callback: () => {
              promoteToConversationNote()
            },
          },
        ],
      })
    }
    if (isOwner.value && props.isEditable) {
      menuItems.push({
        items: [
          {
            id: 'delete',
            leadingIcon: 'delete',
            destructive: true,
            title: 'Löschen',
            callback: () => {
              openDeleteCommentDialog()
            },
          },
        ],
      })
    }
    return menuItems
  })
</script>
